<template>
  <div class="badge-filter">
    <div class="badge-filter__row">
      <div class="filter-group">
        <button
          :label="$string('plans.reset_filter')"
          class="filter-chip"
          :class="{
            'filter-chip--active': !hasActiveFilters,
          }"
          type="button"
          @click="
            () => {
              if (hasActiveFilters) {
                $emit('reset-filters');
              }
            }
          "
        >
          <span>{{ $string('plans.all_results') }}</span>
        </button>
        <button
          v-for="filterValue of filterValues"
          :key="filterValue"
          class="filter-chip"
          :class="{
            'filter-chip--active': filters[filterKey]?.includes(filterValue),
          }"
          type="button"
          @click="
            () => {
              $emit('toggle-filter', { key: filterKey, option: filterValue });
            }
          "
        >
          <span v-if="filterValue.length < 4">{{
            `${prefix}${filterValue}${suffix}`
          }}</span>
          <span v-else>{{ filterValue }}</span>
        </button>
      </div>
    </div>
    <div v-if="showFilter && totalItemsLabel" class="badge-filter__row">
      <p
        class="text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
        v-text="totalItemsLabel"
      />
    </div>
    <div
      v-if="itemsCount < 1 && hasActiveFilters && !loading"
      class="text-mobile-paragraph-s-bold text-desktop-paragraph-s-bold"
      v-html="noResultMessage"
    />
  </div>
</template>

<script>
/**
 * Vue: badge-filter (badge-filter)
 * -----------------------------------------------------------------------------
 * Filter component for products to show applied filters. Also show
 * current results.
 * @param {Object} filters - All filters for check active/inactive
 * @param {Object} filterOptions - All filter options as object with key value
 * @param {Boolean} showFilter theme setting to show filter
 * @param {Number} itemsCount Number of filter results
 * @param {String} totalItemsLabel - String that includes total items count
 * @param {String} noResultMessage - String for no results found message
 * @param {String} prefix - String that can be added before filter value display
 * @param {String} suffix - String that can be added after filter value display
 *
 * @emits toggle-filter - event on checkbox, toggle filter on/off
 * @emits reset-filters - trigger reset all filters
 */

import { mapActions } from 'pinia';
import useOverlaysStore from '~stores/overlays';

export default {
  name: 'BadgeFilter',

  components: {},

  props: {
    totalItemsLabel: {
      type: String,
      default: '',
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    filterOptions: {
      type: Object,
      default: () => ({}),
    },
    showFilter: {
      type: Boolean,
      default: true,
    },
    itemsCount: {
      type: Number,
      default: 0,
    },
    noResultMessage: {
      type: String,
      required: true,
    },
    prefix: {
      type: String,
      default: '',
    },
    suffix: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    sorting: {
      type: String,
      default: 'Ascending',
    },
  },

  emits: {
    'toggle-filter': (payload) => typeof payload === 'object',
    'reset-filters': () => true,
  },

  data() {
    return {
      isActive: false,
    };
  },

  computed: {
    /**
     * Check if there are active filters.
     * @returns {Boolean}
     */
    hasActiveFilters() {
      return Object.keys(this.filters).some(
        (key) => this.filters[key].length > 0,
      );
    },

    filterKey() {
      return Object.keys(this.filterOptions)[0];
    },

    filterValues() {
      const values = Object.values(this.filterOptions)[0];
      return values && this.sorting === 'Descending'
        ? values.slice().reverse()
        : values;
    },
  },

  methods: {
    /**
     * Map Pinia actions.
     */
    ...mapActions(useOverlaysStore, {
      openOverlay: 'open',
    }),

    /**
     * Opens filter drawer
     */
    openFilterOverlay() {
      this.openOverlay({
        component: 'content-drawer',
        ignoreDismissed: true,
        namespace: 'ContentFilter',
      });
    },
  },
};
</script>

<style lang="scss">
@import './badge-filter';
</style>
